import { useEffect, useRef, useState, type ChangeEvent, type DragEvent } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, FileUp, Printer, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { formatIban } from "@/lib/banking/czechAccount";
import type { IsdocView, IsdocViewParty } from "@/lib/isdoc/isdocView";
import { localePath, makeTr, type LandingLocale } from "@/lib/landing/locale";
import { isWithinFileCap } from "@/lib/tools/limits";
import { track } from "@/lib/tracking/analytics";
import { cn } from "@/lib/utils";

/**
 * The free ISDOC viewer: open an .isdoc / .isdocx and read it as an invoice,
 * print it or save it as PDF through the browser's print dialog. Everything
 * runs in this tab; the reader, the ZIP library and the QR library load only
 * when a file is picked. Nothing is sent anywhere; after consent one
 * `tool_used` event says the viewer was used, with nothing from the file.
 *
 * Text from the file is rendered as React text, never as HTML.
 */

type State =
  | { kind: "idle" }
  | { kind: "reading" }
  | { kind: "error"; message: string }
  | { kind: "done"; fileName: string; view: IsdocView; qr: string | null };

function useFormat(locale: LandingLocale) {
  const intl = locale === "cs" ? "cs-CZ" : "en-GB";
  const money = (amount: number, currency: string) =>
    `${new Intl.NumberFormat(intl, { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount)} ${currency}`;
  const qty = (n: number) => new Intl.NumberFormat(intl, { maximumFractionDigits: 4 }).format(n);
  const date = (iso: string) => {
    const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!m) return iso;
    const [, y, mo, d] = m;
    return locale === "cs"
      ? `${Number(d)}. ${Number(mo)}. ${y}`
      : `${Number(d)}/${Number(mo)}/${y}`;
  };
  return { money, qty, date };
}

export function IsdocViewer({ locale }: { locale: LandingLocale }) {
  const tr = makeTr(locale);
  const inputRef = useRef<HTMLInputElement>(null);
  const [state, setState] = useState<State>({ kind: "idle" });
  const [dragging, setDragging] = useState(false);

  // Printing shows only the invoice (see the print rules in index.css).
  useEffect(() => {
    const after = () => document.body.classList.remove("printing-isdoc");
    window.addEventListener("afterprint", after);
    return () => window.removeEventListener("afterprint", after);
  }, []);

  async function readFile(file: File) {
    if (!isWithinFileCap(file.size)) {
      setState({
        kind: "error",
        message: tr(
          "Soubor je na fakturu ISDOC příliš velký (víc než 10 MB).",
          "The file is too large for an ISDOC invoice (over 10 MB)."
        ),
      });
      return;
    }
    setState({ kind: "reading" });
    try {
      const { readIsdocViewFile, isdocPaymentQr } = await import("@/lib/isdoc/isdocView");
      const view = await readIsdocViewFile(file.name, new Uint8Array(await file.arrayBuffer()));
      const payload = isdocPaymentQr(view);
      let qr: string | null = null;
      if (payload) {
        const QRCode = await import("qrcode");
        qr = await QRCode.toDataURL(payload, { margin: 1, width: 360, errorCorrectionLevel: "M" });
      }
      setState({ kind: "done", fileName: file.name, view, qr });
      track("tool_used", { tool: "isdoc_viewer", output: "view" });
    } catch (error) {
      const message = error instanceof Error ? error.message : "";
      if (message.startsWith("limit_")) {
        setState({
          kind: "error",
          message:
            message === "limit_lines"
              ? tr(
                  "Faktura má víc než 2 000 položek; tolik prohlížeč neotevře.",
                  "The invoice has more than 2,000 items; the viewer does not open that many."
                )
              : tr(
                  "Soubor je po rozbalení příliš velký nebo obsahuje příliš mnoho souborů.",
                  "The file is too large once unpacked, or holds too many files."
                ),
        });
        return;
      }
      const notIsdoc = message === "not_isdoc";
      setState({
        kind: "error",
        message: notIsdoc
          ? tr(
              "Tohle není faktura ISDOC. Prohlížeč otevře soubory .isdoc a .isdocx (případně .xml ve formátu ISDOC).",
              "This is not an ISDOC invoice. The viewer opens .isdoc and .isdocx files (or an .xml in ISDOC format)."
            )
          : tr(
              "Soubor se nepodařilo přečíst. Zkontrolujte, že jde o fakturu ISDOC.",
              "The file could not be read. Check that it is an ISDOC invoice."
            ),
      });
    }
  }

  function onPick(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (file) void readFile(file);
  }

  function onDrop(event: DragEvent<HTMLDivElement>) {
    event.preventDefault();
    setDragging(false);
    const file = event.dataTransfer.files?.[0];
    if (file) void readFile(file);
  }

  function print() {
    document.body.classList.add("printing-isdoc");
    track("tool_used", { tool: "isdoc_viewer", output: "print" });
    window.print();
  }

  return (
    <div className="rounded-xl border bg-card p-5 sm:p-6">
      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        className={cn(
          "flex flex-col items-center justify-center gap-3 rounded-lg border border-dashed px-4 py-10 text-center transition-colors",
          dragging && "border-[hsl(var(--action))] bg-primary/10 dark:border-primary"
        )}
      >
        <FileUp aria-hidden="true" className="size-6 text-muted-foreground" />
        <p className="text-sm text-muted-foreground">
          {tr(
            "Přetáhněte sem fakturu .isdoc nebo .isdocx, nebo",
            "Drop an .isdoc or .isdocx invoice here, or"
          )}
        </p>
        <Button type="button" onClick={() => inputRef.current?.click()}>
          {tr("Vybrat soubor", "Choose a file")}
        </Button>
        <input
          ref={inputRef}
          type="file"
          accept=".isdoc,.isdocx,.xml"
          className="sr-only"
          aria-label={tr("Faktura ISDOC", "ISDOC invoice")}
          onChange={onPick}
        />
      </div>

      <p className="mt-4 flex items-start gap-2 text-sm text-muted-foreground">
        <ShieldCheck
          aria-hidden="true"
          className="mt-0.5 size-4 shrink-0 text-[hsl(var(--action))] dark:text-primary"
        />
        {tr(
          "Soubor se nikam neodesílá: fakturu přečte jen váš prohlížeč a nic se neukládá.",
          "The file is never sent anywhere: only your browser reads the invoice, and nothing is stored."
        )}
      </p>

      <div aria-live="polite">
        {state.kind === "reading" && (
          <p className="mt-5 text-sm text-muted-foreground">
            {tr("Čtu soubor…", "Reading the file…")}
          </p>
        )}
        {state.kind === "error" && (
          <p className="mt-5 rounded-lg border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm">
            {state.message}
          </p>
        )}
        {state.kind === "done" && (
          <div className="mt-6">
            <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-sm">
                <span className="font-medium">{state.fileName}</span>
              </p>
              <Button type="button" onClick={print}>
                <Printer aria-hidden="true" className="mr-2 size-4" />
                {tr("Vytisknout / uložit jako PDF", "Print / save as PDF")}
              </Button>
            </div>
            <IsdocDocument view={state.view} qr={state.qr} locale={locale} />
            <p className="mt-6 rounded-lg border bg-secondary/40 px-4 py-3 text-sm leading-relaxed">
              {tr(
                "Chcete faktury a účtenky párovat s platbami z Fio automaticky? ",
                "Want your invoices and receipts matched with your Fio payments automatically? "
              )}
              <Link
                to={localePath(locale, "/")}
                className="inline-flex items-center gap-1 font-medium text-[hsl(var(--action))] underline-offset-4 hover:underline dark:text-primary"
              >
                {tr("Podívejte se na Zettl", "Take a look at Zettl")}
                <ArrowRight aria-hidden="true" className="size-3.5" />
              </Link>
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

const PAYMENT_MEANS: Record<string, [string, string]> = {
  "10": ["Hotově", "Cash"],
  "20": ["Šekem", "Cheque"],
  "31": ["Převodem", "Bank transfer"],
  "42": ["Převodem na účet", "Bank transfer"],
  "48": ["Kartou", "Card"],
  "49": ["Inkasem", "Direct debit"],
  "50": ["Dobírkou", "Cash on delivery"],
  "97": ["Zápočtem", "Offset"],
};

function kindTitle(view: IsdocView, tr: (cs: string, en: string) => string): string {
  switch (view.kind) {
    case "invoice":
      return view.vatApplicable
        ? tr("Faktura – daňový doklad", "Invoice – tax document")
        : tr("Faktura", "Invoice");
    case "credit_note":
      return tr("Opravný daňový doklad (dobropis)", "Credit note");
    case "debit_note":
      return tr("Opravný daňový doklad (vrubopis)", "Debit note");
    case "proforma":
      return tr("Zálohová faktura", "Proforma invoice");
    case "advance":
      return tr("Daňový doklad k přijaté platbě", "Tax document for a received payment");
    default:
      return tr("Doklad ISDOC", "ISDOC document");
  }
}

function PartyBlock({
  title,
  party,
  tr,
}: {
  title: string;
  party: IsdocViewParty | null;
  tr: (cs: string, en: string) => string;
}) {
  return (
    <div>
      <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{title}</p>
      {party ? (
        <div className="mt-1.5 text-sm leading-relaxed">
          <p className="font-semibold">{party.name || "—"}</p>
          {party.street && <p>{party.street}</p>}
          {(party.postalCode || party.city) && (
            <p>{[party.postalCode, party.city].filter(Boolean).join(" ")}</p>
          )}
          {party.country && <p>{party.country}</p>}
          {party.ico && (
            <p className="mt-1">
              {tr("IČO", "Company ID")}: <span className="tabular-nums">{party.ico}</span>
            </p>
          )}
          {party.dic && (
            <p>
              {tr("DIČ", "VAT ID")}: <span className="tabular-nums">{party.dic}</span>
            </p>
          )}
        </div>
      ) : (
        <p className="mt-1.5 text-sm text-muted-foreground">—</p>
      )}
    </div>
  );
}

/** The invoice itself, as one printable block. */
export function IsdocDocument({
  view,
  qr,
  locale,
}: {
  view: IsdocView;
  qr: string | null;
  locale: LandingLocale;
}) {
  const tr = makeTr(locale);
  const f = useFormat(locale);
  const pay = view.payment;
  const means = pay?.meansCode ? PAYMENT_MEANS[pay.meansCode] : undefined;
  const facts: [string, string][] = [
    [tr("Datum vystavení", "Issue date"), view.issueDate && f.date(view.issueDate)],
    [
      tr("Datum zdanitelného plnění", "Tax point date"),
      view.taxPointDate && f.date(view.taxPointDate),
    ],
    [tr("Datum splatnosti", "Due date"), view.dueDate && f.date(view.dueDate)],
    [tr("Forma úhrady", "Payment"), means ? tr(means[0], means[1]) : (pay?.meansCode ?? "")],
    [tr("Číslo účtu", "Account"), pay?.account ?? ""],
    ["IBAN", pay?.iban ? formatIban(pay.iban) : ""],
    ["BIC", pay?.bic ?? ""],
    [tr("Variabilní symbol", "Variable symbol"), pay?.variableSymbol ?? ""],
    [tr("Konstantní symbol", "Constant symbol"), pay?.constantSymbol ?? ""],
    [tr("Specifický symbol", "Specific symbol"), pay?.specificSymbol ?? ""],
    [tr("Objednávka", "Order"), view.orderNumber],
    [
      tr("Kurz", "Exchange rate"),
      view.currency !== "CZK" && view.rate !== 1
        ? `1 ${view.currency} = ${f.qty(view.rate)} CZK`
        : "",
    ],
  ].filter(([, value]) => value) as [string, string][];

  return (
    <article
      data-isdoc-print
      aria-label={`${kindTitle(view, tr)} ${view.number}`}
      className="rounded-lg border bg-background p-4 sm:p-6"
    >
      <header className="flex flex-col gap-1 border-b pb-4 sm:flex-row sm:items-baseline sm:justify-between">
        <h3 className="text-lg font-semibold tracking-tight">{kindTitle(view, tr)}</h3>
        <p className="text-sm">
          {tr("Číslo", "Number")}:{" "}
          <span className="font-semibold tabular-nums">{view.number || "—"}</span>
        </p>
      </header>

      <div className="grid gap-6 border-b py-5 sm:grid-cols-2">
        <PartyBlock title={tr("Dodavatel", "Supplier")} party={view.seller} tr={tr} />
        <PartyBlock title={tr("Odběratel", "Customer")} party={view.buyer} tr={tr} />
      </div>

      <div className="grid gap-6 border-b py-5 sm:grid-cols-[1fr_auto]">
        <dl className="grid grid-cols-1 gap-x-4 text-sm sm:grid-cols-[auto_1fr] sm:gap-y-1">
          {facts.map(([label, value]) => (
            <div key={label} className="contents">
              <dt className="text-xs text-muted-foreground sm:text-sm">{label}</dt>
              <dd className="mb-2 min-w-0 break-words tabular-nums sm:mb-0">{value}</dd>
            </div>
          ))}
          {pay?.ibanDerived && (
            <dd className="text-xs text-muted-foreground sm:col-span-2">
              {tr(
                "IBAN v souboru není, dopočítali jsme ho z čísla účtu.",
                "The file has no IBAN; it was computed from the account number."
              )}
            </dd>
          )}
        </dl>
        {qr && (
          <figure className="flex flex-col items-center gap-1.5 justify-self-start sm:justify-self-end">
            <img
              src={qr}
              alt={tr("QR platba", "QR payment")}
              className="size-32 rounded bg-white p-1"
            />
            <figcaption className="text-xs text-muted-foreground">
              {tr("QR platba", "QR payment")}
            </figcaption>
          </figure>
        )}
      </div>

      {view.note && <p className="whitespace-pre-line border-b py-4 text-sm">{view.note}</p>}

      {/* Phones: one block per item instead of a table that scrolls sideways. */}
      <ul className="divide-y py-3 text-sm sm:hidden">
        {view.lines.map((line, i) => (
          <li key={i} className="flex items-start justify-between gap-3 py-2.5">
            <div className="min-w-0">
              <p>{line.description || "—"}</p>
              <p className="mt-0.5 text-xs tabular-nums text-muted-foreground">
                {f.qty(line.quantity)} {line.unit} × {f.money(line.unitPrice, view.currency)}
                {view.vatApplicable && ` · ${tr("DPH", "VAT")}\u00a0${f.qty(line.vatRate)}\u00a0%`}
              </p>
            </div>
            <p className="whitespace-nowrap tabular-nums">
              {f.money(view.vatApplicable ? line.gross : line.base, view.currency)}
            </p>
          </li>
        ))}
      </ul>
      <div className="hidden overflow-x-auto py-5 sm:block">
        <table className="w-full text-left text-sm">
          <thead className="border-b text-xs text-muted-foreground">
            <tr>
              <th scope="col" className="py-2 pr-3 font-medium">
                {tr("Položka", "Item")}
              </th>
              <th scope="col" className="py-2 pr-3 text-right font-medium">
                {tr("Množství", "Qty")}
              </th>
              <th scope="col" className="py-2 pr-3 text-right font-medium">
                {tr("Cena za jednotku", "Unit price")}
              </th>
              <th scope="col" className="py-2 pr-3 text-right font-medium">
                {tr("DPH", "VAT")}
              </th>
              <th scope="col" className="py-2 text-right font-medium">
                {tr("Celkem", "Total")}
              </th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {view.lines.map((line, i) => (
              <tr key={i}>
                <td className="py-2 pr-3">{line.description || "—"}</td>
                <td className="whitespace-nowrap py-2 pr-3 text-right tabular-nums">
                  {f.qty(line.quantity)} {line.unit}
                </td>
                <td className="whitespace-nowrap py-2 pr-3 text-right tabular-nums">
                  {f.money(line.unitPrice, view.currency)}
                </td>
                <td className="whitespace-nowrap py-2 pr-3 text-right tabular-nums">
                  {view.vatApplicable ? `${f.qty(line.vatRate)} %` : "—"}
                </td>
                <td className="whitespace-nowrap py-2 text-right tabular-nums">
                  {f.money(view.vatApplicable ? line.gross : line.base, view.currency)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="grid gap-6 border-t pt-5 sm:grid-cols-2">
        {view.vatApplicable && view.vatRecap.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <caption className="mb-2 text-left text-xs font-medium uppercase tracking-wide text-muted-foreground">
                {tr("Rekapitulace DPH", "VAT summary")}
              </caption>
              <thead className="text-xs text-muted-foreground">
                <tr>
                  <th scope="col" className="py-1 pr-3 font-medium">
                    {tr("Sazba", "Rate")}
                  </th>
                  <th scope="col" className="py-1 pr-3 text-right font-medium">
                    {tr("Základ", "Base")}
                  </th>
                  <th scope="col" className="py-1 text-right font-medium">
                    {tr("DPH", "VAT")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {view.vatRecap.map((row) => (
                  <tr key={row.rate}>
                    <td className="py-1 pr-3 tabular-nums">{f.qty(row.rate)} %</td>
                    <td className="whitespace-nowrap py-1 pr-3 text-right tabular-nums">
                      {f.money(row.base, view.currency)}
                    </td>
                    <td className="whitespace-nowrap py-1 text-right tabular-nums">
                      {f.money(row.vat, view.currency)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">
            {view.vatApplicable
              ? ""
              : tr("Dodavatel není plátce DPH.", "The supplier is not VAT registered.")}
          </p>
        )}
        <dl className="grid grid-cols-[1fr_auto] gap-x-4 gap-y-1 text-sm sm:justify-self-end">
          {view.vatApplicable && (
            <>
              <dt className="text-muted-foreground">{tr("Bez DPH", "Without VAT")}</dt>
              <dd className="text-right tabular-nums">
                {f.money(view.totals.base, view.currency)}
              </dd>
              <dt className="text-muted-foreground">{tr("DPH", "VAT")}</dt>
              <dd className="text-right tabular-nums">{f.money(view.totals.vat, view.currency)}</dd>
            </>
          )}
          {view.totals.rounding !== 0 && (
            <>
              <dt className="text-muted-foreground">{tr("Zaokrouhlení", "Rounding")}</dt>
              <dd className="text-right tabular-nums">
                {f.money(view.totals.rounding, view.currency)}
              </dd>
            </>
          )}
          {view.totals.deposits !== 0 && (
            <>
              <dt className="text-muted-foreground">{tr("Uhrazené zálohy", "Deposits paid")}</dt>
              <dd className="text-right tabular-nums">
                {f.money(-view.totals.deposits, view.currency)}
              </dd>
            </>
          )}
          <dt className="pt-1 font-semibold">{tr("K úhradě", "To pay")}</dt>
          <dd className="pt-1 text-right text-base font-semibold tabular-nums">
            {f.money(view.totals.payable, view.currency)}
          </dd>
        </dl>
      </div>

      {view.issuingSystem && (
        <p className="mt-5 text-xs text-muted-foreground">
          {tr("Vystaveno v systému", "Issued in")}: {view.issuingSystem}
        </p>
      )}
    </article>
  );
}
